import { AxiosError, AxiosResponse } from "axios";
import { request } from "http";
import useSwr from "swr";
import { GeneralOperationResult } from "../types/GeneralResponse";
import {
  BankCodes,
  BillPaymentHistoryResponse,
  ChangePinRequest,
  ChangePinResponse,
  ConfirmPaymentI,
  ConfirmPaymentResult,
  CreateWalletRequest,
  CreateWalletResponse,
  FundWalletRequest,
  FundWalletResponse,
  GenerateOtpResponse,
  GetWalletDetailsResponse,
  NameEnquiryRequest,
  ResetWalletPinRequest,
  ResetWalletPinResponse,
  TransactionHistoryRequest,
  TransactionHistoryResponse,
  WalletToBankTransferRequest,
  WalletToBankTransferResponse,
  WalletToWalletTransferRequest,
  WalletToWalletTransferResponse,
} from "../types/Wallet";
import api from "../axios";

export const createWallet = (request: CreateWalletRequest) => {
  const willCreateWallet = new Promise<
    GeneralOperationResult<CreateWalletResponse>
  >((resolve) => {
    api.post(`/api/Payment/createWallet`, request)
      .then((response: AxiosResponse<CreateWalletResponse>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError) => {
        resolve({ error: err });
      });
  });

  return willCreateWallet;
};

export const getWalletDetails = () => {
  const willGetWalletDetails = new Promise<
    GeneralOperationResult<GetWalletDetailsResponse>
  >((resolve) => {

    api.get(
      '/api/Payment/getWalletDetailsByUser'
    )
      .then((response: AxiosResponse<GetWalletDetailsResponse>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError) => {
        resolve({ error: err });
      });
  });

  return willGetWalletDetails;
};

export const WALLET_DETAILS_KEY = "/api/Payment/getWalletDetailsByUser";

// The API answers with Message "01" when the user has no wallet yet.
const isNoWalletError = (error: any) =>
  error?.response?.data?.Message === "01";

// Shared by AuthContext and StateContext, so the details are fetched once.
export const useWalletDetails = (enabled: boolean) => {
  const { data, error } = useSwr<GetWalletDetailsResponse>(
    enabled ? WALLET_DETAILS_KEY : null,
    (url: string) => api.get(url).then((res) => res.data),
    { shouldRetryOnError: false },
  );

  return {
    walletDetails: data?.data,
    hasWallet: data?.data ? true : isNoWalletError(error) ? false : undefined,
    isLoading: enabled && !data && !error,
  };
};

export const fundWallet = (request: FundWalletRequest) => {
  const willFundWallet = new Promise<
    GeneralOperationResult<FundWalletResponse>
  >((resolve) => {
    api.post(`/api/Payment/fundWallet`, request)
      .then((response: AxiosResponse<FundWalletResponse>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError) => {
        resolve({ error: err });
      });
  });

  return willFundWallet;
};

export const getTransactionHistoryByWalletId = (
  pageNumber: number,
  pageSize: any,
  walletId: string) => {
  const TransactionHistory = new Promise<
    GeneralOperationResult<TransactionHistoryResponse>
  >((resolve) => {
    api.get(
        `/api/Payment/TransactionHistoryByUser?PageNumber=${pageNumber}&PageSize=${pageSize}`)
      .then((response: AxiosResponse<TransactionHistoryResponse>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError) => {
        resolve({ error: err });
      });
  });
  return TransactionHistory;
};

interface ConfirmPayment {
  response_code: string;
  response_message: string;
  valid: boolean;
}

export interface ValidationErrors {
  errorMessage: string;
  field_errors: Record<string, string>;
}

export const confirmPayment = async (
  email: string,
  pin: string): Promise<ConfirmPayment> => {
  const response = await api.post(
    '/api/Payment/validatepin',
    { email, pin }
  );

  return response.data;
};

export const ResetWalletPin = (request: ResetWalletPinRequest) => {
  const willResetWalletPin = new Promise<
    GeneralOperationResult<ResetWalletPinResponse>
  >((resolve) => {
    api.post(`/api/Payment/resetpin`, request)
      .then((response: AxiosResponse<ResetWalletPinResponse>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError) => {
        resolve({ error: err });
      });
  });
  return willResetWalletPin;
};

export const WalletToAccountTransfer = (
  request: WalletToBankTransferRequest,
  pin: string
) => {
  const AccountTransfer = new Promise<
    GeneralOperationResult<WalletToBankTransferResponse>
  >((resolve) => {
    api.post(`/api/Payment/WalletToBank`, {...request, pin})
      .then((response: AxiosResponse<WalletToBankTransferResponse>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError) => {
        resolve({ error: err });
      });
  });
  return AccountTransfer;
};

export const WalletToWalletTransfer = (
  request: WalletToWalletTransferRequest,
  pin: string
) => {
  const WalletTransfer = new Promise<
    GeneralOperationResult<WalletToWalletTransferResponse>
  >((resolve) => {
    api.post(`/api/Payment/WalletTransfer`, {...request, pin})
      .then((response: AxiosResponse<WalletToWalletTransferResponse>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError) => {
        resolve({ error: err });
      });
  });
  return WalletTransfer;
};

export const generateOtp = async (): Promise<GenerateOtpResponse> => {
  const { data } = await api.post(
    `/api/Payment/generateOTP`,
    {}
  );
  return data;
};

export const verifyOtp = async (
  otp: string,
  email: string): Promise<GenerateOtpResponse> => {
  const { data } = await api.post(
    `/api/Payment/validateOTP`,
    { otp, email }
  );
  return data;
};

export const ChangePin = async (request: ChangePinRequest) => {
  const ChangeUserPin = new Promise<GeneralOperationResult>((resolve) => {
    api.post(`/api/Payment/ChangePin`, request)
      .then((response: AxiosResponse<ChangePinResponse>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError | any) => {
        resolve({ error: err });
      });
  });
  return ChangeUserPin;
};

export const getBankCodes = async (): Promise<BankCodes> => {
  const { data } = await api.get(
    `/api/Payment/GetBankCodes`
  );
  return data;
};

export const GetBillPaymentDescription = async (req: {paymentReference: string}): Promise<BillPaymentHistoryResponse | any> => {
  try {
    const response = await api.get(
      `/api/UtilityBills/GetBillPaymentHistory?paymentReference=${req.paymentReference}`
    );

    return response.data;
  } catch (err) {
    // @ts-ignore
    let error: AxiosError<ValidationErrors> = err; // cast the error for access
    if (!error.response) {
      throw err;
    }

    return error.response.data;
  }
};

export const NameEnquiry = async (req: {requestBody: NameEnquiryRequest}) => {
  try {
    const response = await api.post(
      `/api/Payment/NameEnquiry`,
      req.requestBody
    );
    return response.data;
  } catch (err) {
    // @ts-ignore
    let error: AxiosError<ValidationErrors> = err; // cast the error for access
    if (!error.response) {
      throw err;
    }

    return error.response.data;
  }
};

export const confirmPaymentApi = async (
  propertyId: string) => {
  const confirmPayment = new Promise<ConfirmPaymentResult>((resolve) => {
    api
      .post(
        `/api/Payment/ConfirmPayment?PropertyId=${propertyId}`,
        {})
      .then((response: AxiosResponse<ConfirmPaymentI>) => {
        resolve({ data: response.data });
      })
      .catch((err: AxiosError | any) => {
        resolve({ error: err });
      });
  });

  return confirmPayment;
};

